import { gql } from "@apollo/client";
import { UpdateMembershipDocument, MembershipLevelComponentDocument } from "../types";
export const writeMembershipTier = (tierId, newTier, client) => {
    client.cache.modify({
        id: `MembershipTier:${tierId}`,
        fields: {
            discounts: () => {
                return [{
                        __typename: 'Discount',
                        scope: 'RETAIL',
                        percentage: newTier.discount,
                    }];
            },
            plans: () => {
                return [{
                        __typename: 'Plan',
                        kind: 'MONTHLY',
                        price: {
                            __typename: 'Price',
                            amount: newTier.price
                        },
                    }, {
                        __typename: 'Plan',
                        kind: 'ANNUAL',
                        price: {
                            __typename: 'Price',
                            amount: newTier.price * 12
                        },
                    }];
            },
        },
    });
};
export const saveMemberships = async (businessId, client, options, tierIds) => {
    const tiers = client.readFragment({
        id: `Business:${businessId}`,
        fragment: gql `
      fragment SaveMemberships on Business {
        membershipTiers(kind:Live) {
          id
          name
          defaultMode
          overridePlatformProductSync
          plans {
            kind
            price {
              amount
            }
          }
          discounts {
            scope
            percentage
            guests
          }
          earlyAccess
          birthdayCouponAmount
          birthdayCouponKind
          welcomeGift {
            __typename
            id
            name
            shippingNotesInstructions
            products {
              id
              name
              platformId
              platformName
              platformVariantsOnlyMode
              variants {
                id
                description
                additionalCost {
                  amount
                }
                platformName
                platformId
              }
            }
            variants {
              id
              description
              additionalCost {
                amount
              }
              platformName
              platformId
            }
          }
          benefits {
            __typename
            id
            name
            ...on RegularBenefit {
              order
            }
            ...on SubscriptionBenefit {
              delivery
              nextShipmentDate
              isAddonOnly
              shippingFrequency {
                number
                period
              }
              deliveryFrequency
              firstShipmentDelay
              shippingIncluded
              shippingWeight
              shippingNotesInstructions
              curationMatchMode
              curationPreferOverstock
              products {
                id
                name
                platformId
                platformName
                platformVariantsOnlyMode
                curationValue
                curationCollections
                curationExcludeProducts
                variants {
                  id
                  description
                  additionalCost {
                    amount
                  }
                  platformName
                  platformId
                  curationExcludeProducts
                  curationIncludeVariants
                  curationSearchTerms
                  curationStatus
                }
              }
              variants {
                id
                description
                additionalCost {
                  amount
                }
                platformName
                platformId
              }
              addons {
                id
                description
                applyDiscount
                dontRollOver
                platformId
                platformName
                additionalCost {
                  amount
                }
              }
            }
          }
        }
      }
    `,
    });
    if (!tiers) {
        console.error("Tiers not saved; could not fetch from cache");
        if (process.env.NODE_ENV === "development") {
            throw new Error("Could not save membership tiers");
        }
        else
            return;
    }
    const tiersToSave = tierIds ? tiers.membershipTiers.filter(tier => tierIds.includes(tier.id)) : tiers.membershipTiers;
    await Promise.all(tiersToSave.map((tier) => {
        const membershipInput = Object.assign(Object.assign({}, tier), { name: tier.name || 'New Membership', welcomeGift: tier.welcomeGift ? Object.assign(Object.assign({}, tier.welcomeGift), { name: tier.welcomeGift.name || 'Welcome Gift' }) : null, benefits: (tier.benefits || []).map(b => {
                let newBenefit = Object.assign({}, b);
                if ('delivery' in newBenefit) {
                    // https://stackoverflow.com/questions/44147937/property-does-not-exist-on-type-never
                    if (newBenefit != null)
                        newBenefit['name'] = newBenefit['name'] || 'Curated item from our collection';
                    return {
                        subscription: newBenefit,
                    };
                }
                else {
                    return { regular: newBenefit };
                }
            }) });
        if (options.hasOwnProperty('makeNonDefault')) {
            membershipInput.defaultMode = !options.makeNonDefault;
        }
        return client.mutate({
            mutation: UpdateMembershipDocument,
            variables: {
                id: businessId,
                membershipInput
            },
            refetchQueries: [MembershipLevelComponentDocument],
            update: (cache, { data: { updateOrCreateMembership } }) => {
                // Update tier name in cache
                cache.modify({
                    id: `MembershipTier:${updateOrCreateMembership.id}`,
                    fields: {
                        name: () => updateOrCreateMembership.name,
                    }
                });
                // Update benefit name in cache
                if (updateOrCreateMembership.subscriptionBenefit)
                    cache.modify({
                        id: `SubscriptionBenefit:${updateOrCreateMembership.subscriptionBenefit.id}`,
                        fields: {
                            name: () => updateOrCreateMembership.subscriptionBenefit.name,
                        }
                    });
                // Update welcome gift name in cache
                if (updateOrCreateMembership.welcomeGift)
                    cache.modify({
                        id: `WelcomeGiftBenefit:${updateOrCreateMembership.welcomeGift.id}`,
                        fields: {
                            name: () => updateOrCreateMembership.welcomeGift.name,
                        }
                    });
            }
        });
    }));
};
export const processUpload = (currentFile) => {
    if (!(currentFile === null || currentFile === void 0 ? void 0 : currentFile.originalImageInfo))
        return;
    const params = {
        name: currentFile === null || currentFile === void 0 ? void 0 : currentFile.name,
        mimeType: currentFile === null || currentFile === void 0 ? void 0 : currentFile.mimeType,
        path: currentFile === null || currentFile === void 0 ? void 0 : currentFile.cdnUrl,
    };
    return params;
};
